import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { tracking } from '@iwoca/frontend-tracking-library';
import { FormattedMessage } from 'react-intl';

import { ContinueButton } from './ContinueButton';
import { ContinueHelperText } from './ContinueHelperText';
import { Divider } from './Divider';
import { FullTermDurationRow } from './FullTermDurationRow';
import { FullTermRepaymentRow } from './FullTermRepaymentRow';
import { InterestRateRow } from './InterestRateRow';
import { MinMonthlyRepaymentRow } from './MinMonthlyRepaymentRow';
import { Title } from './Title';
import { ViewPaymentScheduleLink } from './ViewPaymentScheduleLink/ViewPaymentScheduleLink';
import { WithdrawalAmountRow } from './WithdrawalAmountRow';
import { WithdrawalFeesRow } from './WithdrawalFeesRow/WithdrawalFeesRow';
import { isSelectedAmountValid } from '../../../../../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';
import { useSaveOfferStep } from '../../../../../../offerFlowMemory/useSaveOfferStep';
import { RepaymentCalculatorButton } from '../../../../components/LoanSummary/RepaymentCalculatorButton/RepaymentCalculatorButton';
import { useGoToFundingRequirements } from '../../../hooks/useGoToFundingRequirements';

export const SummaryLayout = ({
    selectedOffer,
    drawdownAmount,
    monthlyRepayment,
    fullTermRepayment
}: {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number;
    monthlyRepayment: number | null;
    fullTermRepayment: number | null;
}) => {
    const goToFundingRequirements = useGoToFundingRequirements();
    const saveOfferStep = useSaveOfferStep();

    const canContinue = isSelectedAmountValid(
        drawdownAmount,
        selectedOffer ?? null
    );

    const handleContinue = () => {
        if (!selectedOffer?.offer_id) return;

        tracking.ampli.offersContinueCtaClicked({
            offer_id: selectedOffer.offer_id,
            duration: getDurationInMonths(selectedOffer.duration!)
        });

        saveOfferStep({
            formalOffer: selectedOffer,
            step: 'funding_requirements',
            amount: drawdownAmount
        });

        goToFundingRequirements({
            offerId: selectedOffer.offer_id,
            amount: drawdownAmount
        });
    };

    return (
        <div className="space-y-l">
            <div className="space-y-s">
                <Title />
                <MinMonthlyRepaymentRow amount={monthlyRepayment} />
            </div>

            <Divider />

            <div className="space-y-s">
                <WithdrawalAmountRow drawdownAmount={drawdownAmount} />
                <InterestRateRow selectedOffer={selectedOffer} />
                <WithdrawalFeesRow selectedOffer={selectedOffer} />
            </div>

            <RepaymentCalculatorButton
                selectedOffer={selectedOffer}
                drawdownAmount={drawdownAmount}
            />

            <div className="space-y-s">
                <FullTermDurationRow selectedOffer={selectedOffer} />
                <FullTermRepaymentRow amount={fullTermRepayment} />
                <ViewPaymentScheduleLink
                    selectedOffer={selectedOffer}
                    drawdownAmount={drawdownAmount}
                />
            </div>

            <div className="space-y-l pt-l">
                <ContinueButton
                    disabled={!canContinue}
                    onClick={handleContinue}
                >
                    <FormattedMessage
                        id="_.loan_summary.continue_button"
                        defaultMessage="Continue to final checks"
                    />
                </ContinueButton>
                <ContinueHelperText />
            </div>
        </div>
    );
};
